import React, { useContext } from 'react';
import router from 'next/router';
import { isAfter, subMonths, parse } from 'date-fns';
import PropTypes from 'prop-types';
import { interpolate } from '@format/interpolate';
import { ServerSideErrorModal, NoRecipesFound } from '../../components';
import { EmptyHeader } from '../../molecules';
import { PrefixedReporterProvider } from '../../reporter';
import {
  getNumStarterBoxWeeks,
  getStarterBoxDryConfig,
  getStarterBoxFreshConfig,
  getStarterBoxProductTypeConfig,
  getVariantsSortedByPrice,
  getVariantByRecipes,
  truncatePrice,
  getPricePropertyName,
} from '../../products';
import { getAge, getBreed } from '../../pets';
import { ApiResult, useSession, wrapErrorWithResult } from '../../api';
import { CMSBlok } from '../../cms';
import { UserInfoForm } from '../UserInfoForm';
import { PupInfoForm } from '../PupInfoForm';
import { BirthdayForm } from '../BirthdayForm';
import { BreedForm } from '../BreedForm';
import { CaloriesForm } from '../CaloriesForm';
import { ActivityForm } from '../ActivityForm';
import { AllergiesForm } from '../AllergiesForm';
import { PlanSelectionContainer } from '../PlanSelectionContainer';
import { PlanTypeSelectionForm } from '../PlanTypeSelectionForm';
import { FreshRecipesSelectionForm } from '../FreshRecipesSelectionForm';
import { DryRecipesSelectionForm } from '../DryRecipesSelectionForm';
import { AddOnSelectionForm } from '../AddOnSelectionForm';
import { CheckoutContainer } from '../CheckoutContainer';
import { CheckoutConfirmationContainer } from '../CheckoutConfirmationContainer';
import {
  useExperiment,
  EXTRAS_PRICING_CUSTOMER_GROUP_ID,
} from '../../experiments';
import { QuizHeader } from '../QuizHeader';
import { useOffer } from '../../offer';
import { PoopForm } from '../PoopForm';
import { ALLERGIES_SLUG } from '../routing';
import { STATE, ACTIONS, levelToLabel, DENTAL_CHEWS_SKUS } from './constants';
import { Main, Wrapper } from './styles';
import { OnboardingContext } from './context';
import { interpolations } from './interpolation';
import {
  getPrices,
  getHighestDailyCalories,
  onCartSubmission,
  getLowestCostVariant,
  showNoRecipeFound,
} from './functions';
import { isCompletePet } from './guard';

const QuizContainer = ({ stepNumber, children }) => {
  const { showModal, setShowModal } = useContext(OnboardingContext);
  return (
    <Wrapper>
      <QuizHeader currentStep={stepNumber} totalSteps={8} />
      <Main>{children}</Main>
      <ServerSideErrorModal
        shown={showModal}
        headerText="Hmm, something's not right."
        errorMessage="We weren't able to complete your request, please refresh the page and try again."
        onCloseModal={() => setShowModal(false)}
      />
    </Wrapper>
  );
};

QuizContainer.propTypes = {
  stepNumber: PropTypes.number.isRequired,
  children: PropTypes.object.isRequired,
};

const UserInfo = props => {
  const {
    customer,
    pet,
    send,
    getCustomer,
    updateCustomer,
    goToLogin,
    reporter,
    setShowModal,
    petsApi,
    cartApi,
    quizStepNumbers,
    savePet,
    goBack,
    deleteLocalPet,
  } = useContext(OnboardingContext);
  const { service: sessionService } = useSession();
  const { value: extrasPricingCustomerGroupId } = useExperiment(
    EXTRAS_PRICING_CUSTOMER_GROUP_ID,
  );

  const defaultValues = {
    firstName: customer.first_name || '',
    email: customer.email || '',
  };

  const saveCustomer = async ({ firstName, email }) => {
    if (customer.id) {
      try {
        const response = await updateCustomer({
          first_name: firstName,
          email,
        });

        return {
          action: response.status === 201 ? 'create' : 'update',
          customer: response.data,
        };
      } catch (err) {
        const result = wrapErrorWithResult(err);
        if (result.error.type !== 'emailConflict') {
          throw err;
        }
      }
    }
    await sessionService.register(
      firstName,
      email,
      extrasPricingCustomerGroupId,
    );
    const dataResponse = await getCustomer(sessionService?.user?.id);
    return {
      action: sessionService.user.is_new ? 'create' : 'update',
      customer: dataResponse.data,
    };
  };

  const onSubmit = async formData => {
    let result;
    try {
      let payload = await saveCustomer(formData);
      // handles returning customer that has cleared cookies
      if (payload.action === 'update') {
        const petResponse = await petsApi.listPets();
        let inProcessPet, pets;
        if (!!petResponse.data.results.length) {
          pets = petResponse.data.results;
          inProcessPet =
            petResponse.data.results.find(pet => !isCompletePet(pet)) ??
            petResponse.data.results[0];
        }
        const cartResponse = await cartApi.refreshCart();
        payload = {
          ...payload,
          inProcessPet: inProcessPet,
          pets: pets,
          cart: cartResponse.payload,
        };
      } else {
        const petResponse = await savePet(pet);
        const cartResponse = await cartApi.refreshCart();
        payload = {
          ...payload,
          pet: petResponse.data,
          cart: cartResponse.payload,
        };
      }

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        deleteLocalPet?.();
        const petsWhenLocal = payload.pet ? [payload.pet] : [];
        send(
          ACTIONS.customerInfoSaved({
            customer: payload.customer,
            // payload.inProcessPet is present on 'update', payload.pet is present on 'create'
            pet: payload.inProcessPet ?? payload.pet ?? {},
            pets: payload.pets ?? petsWhenLocal,
            cart: payload.cart ?? null,
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 0,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.userInfo}>
      <UserInfoForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PupInfo = props => {
  const {
    pet,
    savePet,
    send,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const defaultValues = {
    gender: pet.gender,
    name: pet.name,
  };

  const onSubmit = async ({ gender, name }) => {
    let result;
    let payload;
    try {
      const response = await savePet({ gender, name });
      payload = {
        action: response.status === 201 ? 'create' : 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toBirthday({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 1,
  };

  const goBack = () => router.push('/');

  return (
    <QuizContainer stepNumber={quizStepNumbers.pupInfo}>
      <PupInfoForm
        {...cmsProps}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
      />
    </QuizContainer>
  );
};

const Birthday = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const [year, month, day] = pet.birth_date?.split('-') ?? ['', '', ''];
  const defaultValues = {
    year: year,
    month: month,
    day: day,
  };

  const onSubmit = async ({ year, month, day }) => {
    const birthDate = `${year}-${month}-${+day || 1}`;
    let result, payload;
    try {
      const response = await savePet({ birth_date: birthDate });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toBreed({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 2,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.birthday}>
      <BirthdayForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Breed = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
    isLocalPet,
  } = useContext(OnboardingContext);

  const defaultValues = {
    breeds:
      pet.breeds?.map(breed => ({
        value: breed.id,
        label: breed.name,
      })) ?? [],
  };

  const onSubmit = async ({ breeds }) => {
    const newBreeds = breeds.map(breed =>
      isLocalPet ? { id: breed.value, name: breed.label } : breed.value,
    );
    let result, payload;
    try {
      const response = await savePet({ breeds: newBreeds });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toCalories({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 3,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.breeds}>
      <BreedForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Calories = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const defaultValues = {
    weight: pet.weight,
    idealWeight: pet.ideal_weight,
    spayedStatus: pet.is_neutered,
  };

  const onSubmit = async ({ weight, idealWeight, spayedStatus }) => {
    let result, payload;
    try {
      const response = await savePet({
        ideal_weight: idealWeight,
        weight,
        is_neutered: spayedStatus,
      });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toActivity({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 4,
  };

  const isPuppy = isAfter(
    parse(pet.birth_date, 'yyyy-MM-dd', new Date()),
    subMonths(Date.now(), 12),
  );

  return (
    <QuizContainer stepNumber={quizStepNumbers.calories}>
      <CaloriesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
        isPuppy={isPuppy}
      />
    </QuizContainer>
  );
};

const Activity = props => {
  const {
    pet,
    savePet,
    send,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const defaultValues = {
    activity: pet.activity_level,
  };

  const onSubmit = async ({ activity }) => {
    let result, payload;
    try {
      const response = await savePet({ activity_level: activity });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(ACTIONS.toAllergies({ pet: payload.pet }));
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 5,
  };
  return (
    <QuizContainer stepNumber={quizStepNumbers.activity}>
      <ActivityForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Allergies = props => {
  const {
    customer,
    pet,
    savePet,
    cart,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    send,
    petsApi,
    isLocalPet,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const defaultValues = {
    allergies:
      pet.allergies?.map(({ id, name }) => ({
        label: name,
        value: id,
      })) ?? [],
  };

  const onSubmit = async ({ allergies }) => {
    const allergyIds = allergies.map(({ value, label }) =>
      isLocalPet ? { id: value, name: label } : value,
    );
    let result, payload;
    try {
      const response = await savePet({
        allergies: allergyIds,
      });

      let petResponse;
      // Pet 2 and beyond will not be local
      if (!isLocalPet) petResponse = await petsApi.listPets();
      // First pet will be local
      else petResponse = { data: [response.data] };

      payload = {
        action: 'update',
        pet: response.data,
        pets: petResponse?.data.results,
      };

      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(
          ACTIONS.toPoop({
            customer,
            pet: payload.pet,
            cart,
            ...(payload.pets && { pets: payload.pets }),
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 6,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.allergies}>
      <AllergiesForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const Poop = props => {
  const {
    pet,
    savePet,
    send,
    cart,
    customer,
    goBack,
    goToLogin,
    reporter,
    setShowModal,
    quizStepNumbers,
  } = useContext(OnboardingContext);

  const defaultValues = {
    stoolType: pet.stool_type ?? null,
  };

  const onSubmit = async ({ stoolType }) => {
    let result, payload;
    try {
      const response = await savePet({ stool_type: stoolType });
      payload = {
        action: 'update',
        pet: response.data,
      };
      result = ApiResult.success(payload);
    } catch (err) {
      result = wrapErrorWithResult(err);
    }

    ApiResult.match(result, {
      success: async payload => {
        send(
          ACTIONS.toPlanSelection({
            customer,
            pet: payload.pet,
            cart,
          }),
        );
      },
      error: {
        client: error => reporter.error(error.original),
        existingCustomer: () => goToLogin(),
        server: () => setShowModal(true),
      },
    });
    return result;
  };

  const cmsProps = {
    ...interpolate(props, interpolations(pet)),
    index: 7,
  };

  return (
    <QuizContainer stepNumber={quizStepNumbers.poop}>
      <PoopForm
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        goBack={goBack}
        {...cmsProps}
      />
    </QuizContainer>
  );
};

const PlanSelection = ({ planSelectionInfo, body, noRecipesInfo }) => {
  const {
    productTypes,
    selectedPlanType,
    cartProduct,
    pet,
    returnToQuiz,
    goBack,
    send,
    service,
    pets,
    onClickDeletePet,
    pricePeriod,
  } = useContext(OnboardingContext);

  const showRemovePetButton = pets?.length > 1;
  const onDeletePet = () => {
    onClickDeletePet(pet.id);
  };

  if (showNoRecipeFound(productTypes)) {
    return (
      <Wrapper>
        <EmptyHeader />
        <NoRecipesFound
          {...interpolate(noRecipesInfo, {
            pupName: pet?.name,
          })}
          allergies={pet?.allergies.map(({ name }) => ({
            label: name,
          }))}
          onClick={() => returnToQuiz(ALLERGIES_SLUG)}
          onClickDeletePet={showRemovePetButton ? onDeletePet : null}
        />
        {body?.map(content => (
          <CMSBlok key={content._uid} content={content} />
        ))}
      </Wrapper>
    );
  }

  const { defaultProductType, unavailableProductTypes, subscriptionProducts } =
    getStarterBoxProductTypeConfig(productTypes, cartProduct, selectedPlanType);

  const lowestMixedVariant = !!productTypes.mixed.starterBoxes.length
    ? getLowestCostVariant(productTypes.mixed.starterBoxes[0].variants)
    : null;
  const lowestFreshVariant = !!productTypes.fresh.starterBoxes.length
    ? getLowestCostVariant(productTypes.fresh.starterBoxes[0].variants)
    : null;
  const lowestHalfFreshVariant = !!productTypes.half_fresh.starterBoxes.length
    ? getLowestCostVariant(productTypes.half_fresh.starterBoxes[0].variants)
    : null;
  const lowestBakedVariant = !!productTypes.dry.starterBoxes.length
    ? getLowestCostVariant(productTypes.dry.starterBoxes[0].variants)
    : null;

  const pricePerPeriodIndex = getPricePropertyName(pricePeriod);
  const discountedPricePerPeriodIndex = getPricePropertyName(pricePeriod, {
    discounted: true,
  });

  const productTypeInterpolationSubstitutes = {
    pupName: pet.name,
    mixedPricePerWeek: lowestMixedVariant?.[pricePerPeriodIndex],
    freshPricePerWeek: lowestFreshVariant?.[pricePerPeriodIndex],
    halfFreshPricePerWeek: lowestHalfFreshVariant?.[pricePerPeriodIndex],
    bakedPricePerWeek: lowestBakedVariant?.[pricePerPeriodIndex],
    mixedDiscountPricePerWeek:
      lowestMixedVariant?.[discountedPricePerPeriodIndex],
    freshDiscountPricePerWeek:
      lowestFreshVariant?.[discountedPricePerPeriodIndex],
    halfFreshDiscountPricePerWeek:
      lowestHalfFreshVariant?.[discountedPricePerPeriodIndex],
    bakedDiscountPricePerWeek:
      lowestBakedVariant?.[discountedPricePerPeriodIndex],
  };

  const onSubmit = async planType => {
    send(ACTIONS.selectedPlanType({ planType }));
    return onCartSubmission(service);
  };

  return (
    <PlanSelectionContainer body={body}>
      <PlanTypeSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupCalories: parseInt(
            getHighestDailyCalories(subscriptionProducts),
            10,
          ),
        })}
        planTypes={interpolate(
          planSelectionInfo.items,
          productTypeInterpolationSubstitutes,
        )}
        collapsedPlanTypes={interpolate(
          planSelectionInfo.collapsedItems,
          productTypeInterpolationSubstitutes,
        )}
        defaultPlanType={defaultProductType}
        unavailablePlanTypes={unavailableProductTypes}
        returnToQuiz={returnToQuiz}
        goBack={goBack}
        onSubmit={onSubmit}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

PlanSelection.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
  noRecipesInfo: PropTypes.object,
};

const Fresh = ({ planSelectionInfo, body, noRecipesInfo }) => {
  const {
    reporter,
    productTypes,
    selectedPlanType,
    cartProduct,
    setShowModal,
    setLocalSelection,
    offer,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pets,
    onClickDeletePet,
    returnToQuiz,
    pricePeriod,
    goBack,
  } = useContext(OnboardingContext);

  const showRemovePetButton = pets?.length > 1;
  const onDeletePet = () => {
    onClickDeletePet(pet.id);
  };

  if (showNoRecipeFound(productTypes)) {
    return (
      <Wrapper>
        <EmptyHeader />
        <NoRecipesFound
          {...interpolate(noRecipesInfo, {
            pupName: pet?.name,
          })}
          allergies={pet?.allergies.map(({ name }) => ({
            label: name,
          }))}
          onClick={returnToQuiz}
          onClickDeletePet={showRemovePetButton ? onDeletePet : null}
        />
        {body?.map(content => (
          <CMSBlok key={content._uid} content={content} />
        ))}
      </Wrapper>
    );
  }

  const { defaultRecipes, product, unavailableRecipes } =
    getStarterBoxFreshConfig(productTypes[selectedPlanType], cartProduct, null);

  const onSubmit = async freshRecipes => {
    send(
      ACTIONS.selectedFreshRecipes(
        { recipes: freshRecipes },
        {
          productTypes,
          cartProduct,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );
    return onCartSubmission(service);
  };

  const getConfigFromRecipes = selectedRecipes => {
    if (!selectedRecipes.length)
      return { message: 'Choose one or more recipes.' };
    const variants = getVariantsSortedByPrice(product.variants);
    const newVariant = getVariantByRecipes(variants, {
      fresh: selectedRecipes,
      dry: product.variants[0].recipes.dry,
    });
    const { price, discountedPrice } = getPrices(
      offer,
      newVariant,
      pricePeriod,
    );

    return {
      price,
      discountedPrice,
    };
  };

  const checkoutBarHeadline =
    pet.name.toUpperCase() +
    "'S " +
    product.weeks_of_food +
    '-WEEK STARTER BOX';

  return (
    <PlanSelectionContainer body={body}>
      <FreshRecipesSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupAge: getAge(pet.birth_date),
          pupActivity: levelToLabel[pet.activity_level],
          pupBreed: getBreed(pet.breeds),
          pupAllergies: pet.allergies,
          numStarterBoxWeeks: getNumStarterBoxWeeks(
            productTypes[selectedPlanType],
          ),
        })}
        checkoutBarHeadline={checkoutBarHeadline}
        defaultRecipes={defaultRecipes}
        onSubmit={onSubmit}
        onBackButtonClick={goBack}
        unavailableRecipes={unavailableRecipes}
        getConfigFromRecipes={getConfigFromRecipes}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

Fresh.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
  noRecipesInfo: PropTypes.object,
};

const Baked = ({ planSelectionInfo, body }) => {
  const {
    reporter,
    productTypes,
    selectedPlanType,
    selectedFreshRecipes,
    cartProduct,
    setShowModal,
    setLocalSelection,
    offer,
    pet,
    pet_id: petId,
    cartApi,
    send,
    service,
    pricePeriod,
    goBack,
  } = useContext(OnboardingContext);

  const { defaultRecipes, product, unavailableRecipes } =
    getStarterBoxDryConfig(productTypes[selectedPlanType], cartProduct);

  const onSubmit = async dryRecipes => {
    send(
      ACTIONS.selectedBakedRecipes(
        { recipes: dryRecipes },
        {
          productTypes,
          cartProduct,
          cartApi,
          reporter,
          petId,
          setShowModal,
          setLocalSelection,
        },
      ),
    );

    return onCartSubmission(service);
  };

  const getConfigFromRecipes = selectedRecipes => {
    if (!selectedRecipes.length)
      return { message: 'Choose one or more recipes.' };
    const variants = getVariantsSortedByPrice(product.variants);
    const newVariant = getVariantByRecipes(variants, {
      dry: selectedRecipes,
      fresh: selectedFreshRecipes,
    });

    const { price, discountedPrice } = getPrices(
      offer,
      newVariant,
      pricePeriod,
    );

    return {
      price,
      discountedPrice,
    };
  };

  const checkoutBarHeadline =
    pet.name.toUpperCase() +
    "'S " +
    product.weeks_of_food +
    '-WEEK STARTER BOX';

  return (
    <PlanSelectionContainer body={body}>
      <DryRecipesSelectionForm
        {...interpolate(planSelectionInfo, {
          pupName: pet.name,
          pupWeight: pet.weight,
          pupAge: getAge(pet.birth_date),
          pupActivity: levelToLabel[pet.activity_level],
          pupBreed: getBreed(pet.breeds),
          pupAllergies: pet.allergies,
          numStarterBoxWeeks: getNumStarterBoxWeeks(
            productTypes[selectedPlanType],
          ),
        })}
        checkoutBarHeadline={checkoutBarHeadline}
        defaultRecipes={defaultRecipes}
        onSubmit={onSubmit}
        onBackButtonClick={goBack}
        unavailableRecipes={unavailableRecipes}
        getConfigFromRecipes={getConfigFromRecipes}
        period={pricePeriod}
      />
    </PlanSelectionContainer>
  );
};

Baked.propTypes = {
  planSelectionInfo: PropTypes.object.isRequired,
  body: PropTypes.array.isRequired,
};

const Addons = ({
  headline,
  subheadline,
  body,
  addonSections,
  modals,
  desktopCtaText,
}) => {
  const {
    reporter,
    goBack,
    setShowModal,
    pet,
    pet_id: petId,
    cartApi,
    send,
    selectedAddons: defaultAddons,
    treatProducts,
    supplementProducts,
  } = useContext(OnboardingContext);

  const offer = useOffer();

  const filteredTreatProducts = treatProducts.filter(
    treat =>
      !DENTAL_CHEWS_SKUS.includes(treat.sku) || treat.product_recommended,
  );

  const findProductBySku = (products, sku) => {
    return products.find(product => product.sku === sku);
  };

  const mergedAddonSection = addonSections.map(section => {
    const addons = section.addons
      .map(addon => {
        const products =
          addon.productType === 'TR'
            ? filteredTreatProducts
            : supplementProducts;

        const product = findProductBySku(products, addon.sku);

        if (!product) return null;

        return interpolate(addon, {
          addonPrice: truncatePrice(product.price),
          addonDiscount: truncatePrice(product.discounted_price),
          petName: pet.name,
          addonSavePercentage: offer.formattedAmount,
        });
      })
      .filter(Boolean);

    return {
      ...section,
      addons,
    };
  });

  const mergedModals = modals
    .map(modal => {
      const product = findProductBySku(
        [...supplementProducts, ...filteredTreatProducts],
        modal.sku,
      );

      if (!product) return null;

      return interpolate(modal, {
        addonPrice: truncatePrice(product.price),
        addonDiscount: truncatePrice(product.discounted_price),
        addonSavePercentage: offer.formattedAmount,
      });
    })
    .filter(Boolean);

  const unavailableAddons = [
    ...filteredTreatProducts
      .filter(treat => !treat.product_available)
      .map(treat => treat.sku),
    ...supplementProducts
      .filter(supplement => !supplement.product_available)
      .map(supplement => supplement.sku),
  ];

  const onSkip = async () => {
    const addonsToDelete = defaultAddons.filter(addon =>
      unavailableAddons.find(
        unavailableAddon => unavailableAddon === addon.sku,
      ),
    );

    let result = ApiResult.success();
    if (addonsToDelete.length > 0) {
      result = await ApiResult.flatMapAsync(result, () =>
        cartApi.removeFromCart({
          addons: addonsToDelete,
        }),
      );
    }

    ApiResult.match(result, {
      success: () => {
        send(ACTIONS.toCheckout());
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });
  };

  const onSubmit = async selectedAddons => {
    const addonsToDelete = defaultAddons.filter(
      addon =>
        !selectedAddons.find(
          selectedAddon => selectedAddon.sku === addon.sku,
        ) ||
        unavailableAddons.find(
          unavailableAddon => unavailableAddon === addon.sku,
        ),
    );

    let result = ApiResult.success();
    if (addonsToDelete.length > 0) {
      result = await ApiResult.flatMapAsync(result, () =>
        cartApi.removeFromCart({
          addons: addonsToDelete,
        }),
      );
    }
    if (selectedAddons.length > 0) {
      result = await ApiResult.flatMapAsync(result, () =>
        cartApi.addAddonsToCart({
          pet_id: petId,
          addons: selectedAddons.map(addon => ({
            sku: addon.sku,
            frequency: addon.frequency,
            quantity: addon.quantity,
          })),
        }),
      );
    }

    ApiResult.match(result, {
      success: () => {
        send(ACTIONS.toCheckout());
      },
      error: {
        client: error => {
          reporter.error(error.original);
          setShowModal(true);
        },
        server: () => setShowModal(true),
      },
    });

    return result;
  };

  return (
    <PlanSelectionContainer body={body}>
      <AddOnSelectionForm
        headline={interpolate(headline, { pupName: pet.name })}
        subheadline={subheadline}
        addonSections={mergedAddonSection}
        defaultAddons={defaultAddons}
        unavailableAddons={unavailableAddons}
        onSubmit={onSubmit}
        onSkipButtonClick={onSkip}
        onBackButtonClick={goBack}
        submitButtonText={desktopCtaText}
        modals={mergedModals}
      />
    </PlanSelectionContainer>
  );
};

Addons.propTypes = {
  headline: PropTypes.string,
  subheadline: PropTypes.string,
  body: PropTypes.array,
  addonSections: PropTypes.array,
  desktopCtaText: PropTypes.string,
  modals: PropTypes.array,
};

const Checkout = props => {
  return (
    <PrefixedReporterProvider prefix="Checkout">
      <CheckoutContainer {...props} />
    </PrefixedReporterProvider>
  );
};

const Confirmation = props => {
  return (
    <PrefixedReporterProvider prefix="Confirmation">
      <CheckoutConfirmationContainer {...props} />
    </PrefixedReporterProvider>
  );
};

export const components = {
  [STATE.userInfo]: UserInfo,
  [STATE.pupInfo]: PupInfo,
  [STATE.birthday]: Birthday,
  [STATE.breed]: Breed,
  [STATE.calories]: Calories,
  [STATE.activity]: Activity,
  [STATE.allergies]: Allergies,
  [STATE.poop]: Poop,
  [STATE.planSelection]: PlanSelection,
  [STATE.fresh]: Fresh,
  [STATE.baked]: Baked,
  [STATE.addons]: Addons,
  [STATE.checkout]: Checkout,
  [STATE.confirmation]: Confirmation,
};
